import { captureException } from "@sentry/react";
import { Link, useRouteContext } from "@tanstack/react-router";
import { useCallback } from "react";
import ArrowForward from "~icons/material-symbols/arrow-forward";
import ChevronRight from "~icons/material-symbols/chevron-right";
import Info from "~icons/material-symbols/info-outline";
import { css, cx } from "../../../styled-system/css";
import statusCancel from "../../assets/icons/status-cancel.svg";
import { LoadingIcon } from "../../components/loadingIcon";
import { login } from "../../libs/connect";
import { t } from "../../libs/i18n";
import { openUserAuthenticationReregistration } from "../../libs/sdk";
import { HokkaidoR8HouseholderReceiveSuccessModal } from "../home/components/hokkaido-r8-householder-receive-success";
import type {
  HokkaidoMynaApplicationBannerType,
  HokkaidoMynaApplicationFailedReason,
  HokkaidoMynaApplicationResultView,
} from "./state";
import type { TermsDocumentContent } from "./terms";

export function HokkaidoMynaApplicationHomeBanner({
  bannerType,
}: {
  bannerType: HokkaidoMynaApplicationBannerType;
}) {
  if (bannerType === "apply") {
    return (
      <Link
        to="/hkp/apply"
        className={css({
          bg: "surface.accentPrimaryLight",
          border: "1px solid",
          borderColor: "border.secondary",
          rounded: "12px",
          p: "16px",
          display: "flex",
          alignItems: "center",
          gap: "4px",
        })}
      >
        <Info
          className={css({
            color: "text.accentPrimary",
            width: "24px",
            height: "24px",
            flexShrink: 0,
          })}
        />
        <p
          className={css({
            flex: 1,
            fontSize: "16px",
            lineHeight: 1.5,
            color: "text.primary",
          })}
        >
          {t("QRコードが印刷されたハガキが届いていない世帯主の方へ")}
        </p>
        <ChevronRight
          className={css({
            color: "text.accentPrimary",
            width: "24px",
            height: "24px",
            flexShrink: 0,
          })}
        />
      </Link>
    );
  }
  if (bannerType === "checking") {
    return (
      <section
        className={css({
          bg: "surface.accentWarnLight",
          border: "1px solid",
          borderColor: "border.secondary",
          rounded: "12px",
          p: "16px",
          fontSize: "14px",
          lineHeight: 1.4,
          color: "text.secondary",
        })}
      >
        {t("ポイントの受け取り手続きを北海道で確認中です")}
      </section>
    );
  }
  return null;
}

export function PageContent({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={css({
        p: "20px",
        display: "flex",
        flexDir: "column",
        gap: "24px",
      })}
    >
      {children}
    </div>
  );
}

export function PageTitle({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "center";
}) {
  return (
    <h2
      className={css({
        fontSize: "20px",
        fontWeight: 600,
        lineHeight: 1.2,
        color: "text.primary",
        textAlign: align,
      })}
    >
      {children}
    </h2>
  );
}

export function PageBody({ children }: { children: React.ReactNode }) {
  return (
    <p
      className={css({
        fontSize: "16px",
        lineHeight: 1.5,
        color: "text.primary",
        whiteSpace: "pre-wrap",
      })}
    >
      {children}
    </p>
  );
}

export function ButtonGroup({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={css({
        display: "flex",
        flexDir: "column",
        gap: "12px",
      })}
    >
      {children}
    </div>
  );
}

const termsTextStyle = css({
  fontSize: "14px",
  lineHeight: 1.6,
  color: "text.primary",
  whiteSpace: "pre-wrap",
});

export function TermsDocument({
  title,
  preamble,
  articles,
  enactment,
}: TermsDocumentContent) {
  return (
    <section
      className={css({
        p: "16px",
        rounded: "12px",
        border: "1px solid",
        borderColor: "border.primary",
        bg: "surface.primary",
        display: "flex",
        flexDir: "column",
        gap: "16px",
      })}
    >
      <h3
        className={css({
          fontSize: "16px",
          fontWeight: 600,
          textAlign: "center",
          color: "text.primary",
        })}
      >
        {title}
      </h3>
      <p className={termsTextStyle}>{preamble}</p>
      {articles.map((article) => (
        <div
          key={article.heading}
          className={css({ display: "flex", flexDir: "column", gap: "4px" })}
        >
          <h4
            className={css({
              fontSize: "14px",
              fontWeight: 600,
              color: "text.primary",
            })}
          >
            {article.heading}
          </h4>
          {article.paragraphs.map((paragraph) => (
            <div
              key={paragraph.text}
              className={css({ display: "flex", gap: "4px" })}
            >
              {paragraph.label && (
                <span
                  className={css({
                    flexShrink: 0,
                    fontSize: "14px",
                    lineHeight: 1.6,
                    color: "text.primary",
                  })}
                >
                  {paragraph.label}
                </span>
              )}
              <div
                className={css({
                  display: "flex",
                  flexDir: "column",
                  gap: "4px",
                })}
              >
                <p className={termsTextStyle}>{paragraph.text}</p>
                {paragraph.quote && (
                  <p className={cx(termsTextStyle, css({ pl: "16px" }))}>
                    {paragraph.quote}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      ))}
      <p className={cx(termsTextStyle, css({ textAlign: "right" }))}>
        {enactment}
      </p>
    </section>
  );
}

type ButtonProps = {
  children: React.ReactNode;
  onClick: () => void;
  variant: "primary" | "secondary" | "tertiary";
  withArrow?: boolean;
  disabled?: boolean;
};

export function ActionButton({
  children,
  onClick,
  variant,
  withArrow,
  disabled,
}: ButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={css({
        h: "48px",
        w: "full",
        px: "24px",
        rounded: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
        fontSize: "16px",
        fontWeight: 600,
        lineHeight: 1.25,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        ...(variant === "primary" && {
          bg: "surface.accentPrimary",
          color: "text.inverse",
          boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.03)",
        }),
        ...(variant === "secondary" && {
          bg: "slate.50",
          color: "text.primary",
          border: "1px solid",
          borderColor: "border.secondary",
          boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.03)",
        }),
        ...(variant === "tertiary" && {
          color: "text.primary",
        }),
      })}
    >
      {children}
      {withArrow && (
        <ArrowForward className={css({ width: "24px", height: "24px" })} />
      )}
    </button>
  );
}

function MessageCard({
  tone,
  title,
  body,
}: {
  tone: "error" | "warn";
  title: string;
  body: string;
}) {
  return (
    <section
      className={css({
        bg:
          tone === "error"
            ? "surface.accentErrorLight"
            : "surface.accentWarnLight",
        border: "1px solid",
        borderColor: "border.secondary",
        rounded: "12px",
        p: "16px",
        display: "flex",
        flexDir: "column",
        gap: "8px",
      })}
    >
      <p
        className={css({
          fontSize: "16px",
          lineHeight: 1.5,
          color: "text.primary",
        })}
      >
        {title}
      </p>
      <p
        className={css({
          fontSize: "14px",
          lineHeight: 1.4,
          color: "text.secondary",
          whiteSpace: "pre-wrap",
        })}
      >
        {body}
      </p>
    </section>
  );
}

function StatusCancelIcon() {
  return (
    <div
      className={css({
        display: "flex",
        justifyContent: "center",
        py: "8px",
      })}
    >
      <div className={css({ position: "relative", w: "80px", h: "80px" })}>
        <img
          src={statusCancel}
          alt=""
          className={css({
            position: "absolute",
            inset: "-4.17%",
            w: "108.34%",
            h: "108.34%",
            maxW: "none",
          })}
        />
      </div>
    </div>
  );
}

export function ProcessingView() {
  return (
    <div
      className={css({
        display: "flex",
        flexDir: "column",
        alignItems: "center",
        gap: "16px",
        pt: "80px",
        color: "text.primary",
      })}
    >
      <LoadingIcon />
      <p>{t("確認しています。そのままお待ちください")}</p>
    </div>
  );
}

type ResultViewProps = {
  view: HokkaidoMynaApplicationResultView;
  onGoHome: () => void;
  onRetry: () => void;
};

export function HokkaidoMynaApplicationResult({
  view,
  onGoHome,
  onRetry,
}: ResultViewProps) {
  const { tenant } = useRouteContext({ from: "/_authed" });
  const handleOpenReregistration = useCallback(async () => {
    const registration = await openUserAuthenticationReregistration();
    if (registration.result !== "success") {
      return;
    }
    // 照合に使う本人情報は login でしか更新されず、トークンが有効な間は login が省略されるため
    try {
      await login(tenant);
    } catch (e) {
      captureException(e);
    }
    onRetry();
  }, [tenant, onRetry]);

  switch (view.type) {
    case "granted":
      return (
        <HokkaidoR8HouseholderReceiveSuccessModal
          detail={{ amount: view.amount, fundSource: view.fundSource }}
          onClose={onGoHome}
        />
      );
    case "checking":
      return (
        <PageContent>
          <MessageCard
            tone="warn"
            title={t("ポイントの受け取り手続きを受け付けました")}
            body={t(
              "住民票の情報だけではハガキの送付先と照らし合わせられなかったため、北海道で確認しています。確認が終わるとポイントが付与されます。\n\n確認には日数がかかる場合があります。状況はホーム画面でご確認いただけます。",
            )}
          />
          <ActionButton variant="primary" onClick={onGoHome}>
            {t("ホームへ")}
          </ActionButton>
        </PageContent>
      );
    case "notHouseholder":
      return (
        <IneligibleView
          title={t("2026年5月1日時点の世帯主ではないため、受け取れません")}
          body={t(
            "どうみんポイントは、2026年5月1日時点の世帯主の方が受け取れます。世帯主の方ご本人のマイナンバーカードでログインしてお手続きください。",
          )}
          onGoHome={onGoHome}
        />
      );
    case "outsideHokkaido":
      return (
        <IneligibleView
          title={t(
            "2026年5月1日時点で北海道にお住まいでないため、受け取りできませんでした",
          )}
          body={t(
            "どうみんポイントは、2026年5月1日時点で北海道に住民登録がある世帯主の方が受け取れます。",
          )}
          onGoHome={onGoHome}
        />
      );
    case "alreadyReceived":
      // 文言は仮（デザイン未確定）
      return (
        <IneligibleView
          title={t("すでにどうみんポイントを受け取っています")}
          body={t(
            "どうみんポイントは1世帯につき1回受け取れます。残高はホーム画面でご確認ください。",
          )}
          onGoHome={onGoHome}
        />
      );
    case "identityMismatch":
      return (
        <PageContent>
          <MessageCard
            tone="error"
            title={t(
              "マイナンバーカードとアプリの登録情報が一致しませんでした",
            )}
            body={t(
              "ポケットサインアプリの本人情報と、マイナポータルの登録情報が異なっています。\n\nポケットサインアプリに登録されている本人情報が古い場合、「登録情報を更新する」ボタンから、最新の本人情報に更新し、再度、マイナポータルから本認証作業を行なってください。\n\nマイナンバーカードの更新が行われていない場合は役所で更新の手続きを行ってください。",
            )}
          />
          <StatusCancelIcon />
          <div
            className={css({ display: "flex", flexDir: "column", gap: "16px" })}
          >
            <ActionButton
              variant="secondary"
              onClick={handleOpenReregistration}
            >
              {t("登録情報を更新する")}
            </ActionButton>
            <ActionButton variant="primary" onClick={onGoHome}>
              {t("終了する")}
            </ActionButton>
          </div>
        </PageContent>
      );
    case "failed":
      return (
        <PageContent>
          <MessageCard tone="error" {...failedMessage(view.reason)} />
          <StatusCancelIcon />
          <ButtonGroup>
            <ActionButton variant="primary" onClick={onRetry}>
              {t("もう一度手続きする")}
            </ActionButton>
            <ActionButton variant="tertiary" onClick={onGoHome}>
              {t("終了する")}
            </ActionButton>
          </ButtonGroup>
        </PageContent>
      );
    case "serverError":
      return (
        <PageContent>
          <MessageCard
            tone="error"
            title={t("サーバーエラー")}
            body={t("時間をおいて再度お試しください。")}
          />
          <StatusCancelIcon />
          <ActionButton variant="primary" onClick={onGoHome}>
            {t("終了する")}
          </ActionButton>
        </PageContent>
      );
  }
}

function IneligibleView({
  title,
  body,
  onGoHome,
}: {
  title: string;
  body: string;
  onGoHome: () => void;
}) {
  return (
    <PageContent>
      <PageTitle>{title}</PageTitle>
      <PageBody>{body}</PageBody>
      <ActionButton variant="primary" onClick={onGoHome}>
        {t("終了する")}
      </ActionButton>
    </PageContent>
  );
}

function failedMessage(reason: HokkaidoMynaApplicationFailedReason): {
  title: string;
  body: string;
} {
  switch (reason) {
    case "cancelled":
      return {
        title: t("手続きが完了しませんでした"),
        body: t(
          "マイナポータルでの手続きが途中で中止されました。お手数ですが、もう一度お試しください。",
        ),
      };
    case "expired":
      return {
        title: t("手続きが完了しませんでした"),
        body: t(
          "マイナポータルでの手続きが時間内に完了しませんでした。お手数ですが、もう一度お試しください。",
        ),
      };
    case "invalidCard":
      return {
        // MynaConnect はスマホ用電子証明書でのログインも別人のカードと同じエラーで返すため、両方を案内する
        title: t("ご本人のマイナンバーカード本体で手続きしてください"),
        body: t(
          "次のいずれかの方法でマイナポータルにログインしたため、手続きを完了できませんでした。\n・スマートフォンに搭載したマイナンバーカード（スマホ用電子証明書）でログインした\n・このアプリに登録されている方とは別の方のマイナンバーカードでログインした\n\n世帯主の方ご本人のマイナンバーカード本体を読み取って、もう一度お試しください。",
        ),
      };
    case "error":
      return {
        title: t("マイナポータルとの連携でエラーが発生しました"),
        body: t("時間をおいて、もう一度お試しください。"),
      };
  }
}
